'use client';

import React from 'react';
import { cn, Skeleton } from '@sharpr/ui';
import type { Sport } from '@sharpr/types';
import {
  PRIMARY_COL_WIDTH,
  BEST_LINE_COL_WIDTH,
  SPORTSBOOK_COL_WIDTH,
  TABLE_ROW_HEIGHT,
} from '../table/tableLayout';

/**
 * Structural placeholder for the odds view while the bulk player fetch resolves.
 * Pinned to the real loaded layout (measured at 390px + 1440px viewports):
 *
 *   mobile  — search h-7 + Filters pill → Teams header + chip row → market
 *             section(s) → player rows at a 59px pitch (avatar + name line +
 *             matchup line)
 *   desktop — left panel w-[22rem] (search h-[39px], same filter sections,
 *             85px player cards) + right table pane (column-header band,
 *             TABLE_ROW_HEIGHT rows)
 *
 * Sport-aware: MLB shows Teams + confirmed-lineup toggle, "Pitcher Markets"
 * expanded and "Batter Markets" collapsed; NBA shows Teams without the toggle
 * and a single flat "Markets" section (mirrors TeamFilterLocal's
 * showConfirmedToggle and MarketFilterLocal's sport branch).
 *
 * Keep section order/heights in sync with PlayerSelectionPanel; the desktop
 * table geometry comes from components/table/tableLayout.ts, shared with
 * VirtualizedPropTable — the goal is that the data swap-in replaces these
 * placeholders in place, so nothing the user is looking at moves.
 */

const ROW_DIVIDER = 'border-b border-border/40 dark:border-border-primary/30';
const CELL_DIVIDER = 'border-r border-border dark:border-border-secondary';

/** Overfill counts — the real table renders one column per book (often 10+)
 *  and as many rows as the data holds, so the skeleton renders enough of both
 *  to fill ultrawide/tall desktops; overflow-hidden clips the excess. */
const TABLE_BOOK_COLS = 12;
const TABLE_ROWS = 20;

/** Staggers the pulse phase per row. Must sit on each Skeleton (animation-delay
 *  neither inherits nor applies to elements without an animation). */
const rowDelay = (i: number) => ({ animationDelay: `${i * 50}ms` });

function PanelSections({ sport }: { sport: Sport }) {
  return (
    <>
      {/* Search + Filters (input h-7 mobile / h-[39px] desktop; pill mobile / icon circle desktop) */}
      <div className="flex-shrink-0 px-2.5 sm:px-4 pt-2 sm:pt-3 pb-1.5 sm:pb-2 flex items-center gap-2">
        <Skeleton className="flex-1 rounded-lg h-7 md:h-[39px]" />
        <Skeleton className="rounded-full h-7 w-[77px] md:h-[34px] md:w-[34px]" />
      </div>

      {/* Teams: header row (+ MLB confirmed-lineup toggle) then team chip row */}
      <div className="flex-shrink-0 px-2.5 sm:px-4 mt-1">
        <div className="flex items-center justify-between h-6">
          <Skeleton className="h-4 w-16" />
          {sport === 'mlb' && <Skeleton className="h-6 w-28 rounded-full" />}
        </div>
        <div className="flex items-center gap-2 mt-1.5 mb-3 overflow-hidden">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-8 w-14 flex-shrink-0 rounded-lg" />
          ))}
        </div>
      </div>

      {/* Markets: MLB "Pitcher Markets" / NBA flat "Markets" — header + chip row */}
      <div className={cn('flex-shrink-0 px-2.5 sm:px-4', sport !== 'mlb' && 'mb-4')}>
        <div className="flex items-center h-6">
          <Skeleton className="h-4 w-24" />
        </div>
        <div className="flex items-center gap-2 mt-1.5 mb-3 overflow-hidden">
          {[72, 88, 56, 84, 48].map((w, i) => (
            <Skeleton key={i} className="h-[30px] flex-shrink-0 rounded-full" style={{ width: w }} />
          ))}
        </div>
      </div>

      {/* MLB only: Batter Markets collapsed header */}
      {sport === 'mlb' && (
        <div className="flex-shrink-0 px-2.5 sm:px-4 mb-4">
          <div className="flex items-center h-6">
            <Skeleton className="h-4 w-24" />
          </div>
        </div>
      )}

      {/* Player rows — mobile 59px pitch / desktop 85px cards, hairline-divided.
          Overfilled so the list reaches the bottom on tall viewports; the
          flex-1 overflow-hidden container clips the excess. */}
      <div className="flex-1 overflow-hidden px-1 sm:px-2">
        {Array.from({ length: 18 }).map((_, i) => (
          <div
            key={i}
            className={cn('flex items-center gap-2.5 px-1.5 h-[59px] md:h-[85px]', ROW_DIVIDER)}
          >
            <Skeleton
              className="flex-shrink-0 rounded-full h-9 w-9 md:h-10 md:w-10"
              style={rowDelay(i)}
            />
            <div className="flex-1 min-w-0 space-y-1.5">
              <Skeleton className="h-3.5 w-32" style={rowDelay(i)} />
              <Skeleton className="h-3 w-40 max-w-full" style={rowDelay(i)} />
            </div>
            <Skeleton className="h-3 w-16 flex-shrink-0" style={rowDelay(i)} />
          </div>
        ))}
      </div>
    </>
  );
}

export function OddsViewSkeleton({ className, sport }: { className?: string; sport: Sport }) {
  return (
    <div
      className={cn('flex flex-col h-full overflow-hidden', className)}
      role="status"
      aria-label="Loading odds"
    >
      <div className="flex-1 flex overflow-hidden">
        {/* Left panel — full width on mobile, 22rem on desktop (PlayerSelectionPanel) */}
        <div className="flex w-full md:w-[22rem] flex-col min-w-0 md:border-r md:border-gray-200 md:dark:border-border-primary">
          <PanelSections sport={sport} />
        </div>

        {/* Right table pane (desktop only). Column widths + row height shared
            with VirtualizedPropTable via tableLayout; header band 57px is
            emergent from TableHeader's book-logo content. */}
        <div className="hidden md:flex flex-1 flex-col min-w-0 overflow-hidden">
          {/* Column-header band */}
          <div className={cn('flex-shrink-0 h-[57px] flex items-stretch', ROW_DIVIDER)}>
            <div className={cn('flex-shrink-0', CELL_DIVIDER)} style={{ width: PRIMARY_COL_WIDTH }} />
            <div
              className={cn('flex-shrink-0 flex items-center justify-center', CELL_DIVIDER)}
              style={{ width: BEST_LINE_COL_WIDTH }}
            >
              <Skeleton className="h-4 w-[70px]" />
            </div>
            {Array.from({ length: TABLE_BOOK_COLS }).map((_, i) => (
              <div
                key={i}
                className={cn('flex-shrink-0 flex items-center justify-center', CELL_DIVIDER)}
                style={{ width: SPORTSBOOK_COL_WIDTH }}
              >
                <Skeleton className="h-8 w-[88px]" />
              </div>
            ))}
            <div className="flex-1" />
          </div>
          {/* Rows */}
          <div className="flex-1 overflow-hidden">
            {Array.from({ length: TABLE_ROWS }).map((_, i) => (
              <div key={i} className={cn('flex items-stretch', ROW_DIVIDER)} style={{ height: TABLE_ROW_HEIGHT }}>
                {/* Player cell: avatar + name/matchup, market/EV stack at right edge */}
                <div
                  className={cn('flex-shrink-0 flex items-center gap-2.5 px-3', CELL_DIVIDER)}
                  style={{ width: PRIMARY_COL_WIDTH }}
                >
                  <Skeleton
                    className="h-[30px] w-[30px] flex-shrink-0 rounded-full"
                    style={rowDelay(i)}
                  />
                  <div className="flex-1 min-w-0 space-y-1.5">
                    <Skeleton className="h-3.5 w-24" style={rowDelay(i)} />
                    <Skeleton className="h-3 w-28" style={rowDelay(i)} />
                  </div>
                  <div className="flex-shrink-0 space-y-1.5">
                    <Skeleton className="h-3 w-12" style={rowDelay(i)} />
                    <Skeleton className="h-3 w-12 ml-auto" style={rowDelay(i)} />
                  </div>
                </div>
                {/* Best Line cell: stacked over/under lines */}
                <div
                  className={cn('flex-shrink-0 flex flex-col items-center justify-center gap-1.5', CELL_DIVIDER)}
                  style={{ width: BEST_LINE_COL_WIDTH }}
                >
                  <Skeleton className="h-3.5 w-28" style={rowDelay(i)} />
                  <Skeleton className="h-3.5 w-28" style={rowDelay(i)} />
                </div>
                {/* Book cells: stacked over/under lines */}
                {Array.from({ length: TABLE_BOOK_COLS }).map((_, j) => (
                  <div
                    key={j}
                    className={cn('flex-shrink-0 flex flex-col items-center justify-center gap-1.5', CELL_DIVIDER)}
                    style={{ width: SPORTSBOOK_COL_WIDTH }}
                  >
                    <Skeleton className="h-3 w-24" style={rowDelay(i)} />
                    <Skeleton className="h-3 w-24" style={rowDelay(i)} />
                  </div>
                ))}
                <div className="flex-1" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
